import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { getBlacDevices, BlacDevice, useBlacTokenManager, refreshBlacTokenIfNeeded, getTagTrazabilidad } from '../../apis/blacApi';
import { tracksolidApi, TrazabilidadTracksolidPunto } from '../../apis/tracksolidApi';
import { getDispositivosSsemConUbicacion } from '../../apis/dispositivoSsemApi';
import { productosApi, ProductoConUbicacion } from '../../apis/productosDinamicosApi';
import { DispositivoSsemConUbicacion } from '../../interfaces/DispositivoSsemInterface';
import { TrazabilidadPunto } from '../../interfaces/TagBlacInterface';
import { MAP_CONFIG } from '../../config/googleMaps';
import { useGoogleMaps } from '../../context/GoogleMapsContext';
import { RefreshCw, MapPin, Clock, Navigation, Gauge, Smartphone, Route, EyeOff, Package } from 'lucide-react';
import { GoogleMapsProvider } from '../../context/GoogleMapsContext';
import { API } from '../../global/global';

// Extender BlacDevice con campos internos de fuente
interface BlacDeviceConFuente extends BlacDevice {
  _fuente?: 'blac' | 'tracksolid';
  _tracksolidId?: number;
}

const MapaBlackContent: React.FC = () => {
  const { isLoaded, loadError } = useGoogleMaps();
  const { startTokenRefresh, stopTokenRefresh } = useBlacTokenManager();
  const [devices, setDevices] = useState<BlacDeviceConFuente[]>([]);
  const [dispositivosSsem, setDispositivosSsem] = useState<DispositivoSsemConUbicacion[]>([]);
  const [productos, setProductos] = useState<ProductoConUbicacion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<BlacDeviceConFuente | null>(null);
  const [selectedDispositivo, setSelectedDispositivo] = useState<DispositivoSsemConUbicacion | null>(null);
  const [selectedProducto, setSelectedProducto] = useState<ProductoConUbicacion | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [showDeviceType, setShowDeviceType] = useState<'tags' | 'dispositivos' | 'productos'>('productos');
  
  // Estados para trazabilidad
  const [showTrazabilidad, setShowTrazabilidad] = useState(false);
  const [trazabilidadData, setTrazabilidadData] = useState<TrazabilidadPunto[]>([]);
  const [loadingTrazabilidad, setLoadingTrazabilidad] = useState(false);
  const [selectedTagForTrazabilidad, setSelectedTagForTrazabilidad] = useState<number | null>(null);
  const [trazabilidadFuente, setTrazabilidadFuente] = useState<'blac' | 'tracksolid'>('blac');
  const [trazFechaInicio, setTrazFechaInicio] = useState<string>('');
  const [trazFechaFin, setTrazFechaFin] = useState<string>('');
  
  const mapRef = useRef<google.maps.Map | null>(null);
  const nativePolylineRef = useRef<google.maps.Polyline | null>(null); // Polyline nativa
  const nativeMarkersRef = useRef<google.maps.Marker[]>([]); // Marcadores nativos

  // ✅ Función para cargar dispositivos Skyangel con ubicación
  const cargarDispositivosSsem = useCallback(async () => {
    console.log('🔄 Cargando dispositivos Skyangel con ubicación...');
    setLoading(true);
    setError(null);

    try {
      const response = await getDispositivosSsemConUbicacion();
      console.log('📱 Dispositivos Skyangel obtenidos:', response.data);
      
      if (!response.data || response.data.length === 0) {
        setDispositivosSsem([]);
        setError('No hay dispositivos Skyangel con ubicación disponibles');
        setLastUpdate(new Date());
        setLoading(false);
        return;
      }
      
      // Filtrar dispositivos con coordenadas válidas
      const validDispositivos = response.data.filter(dispositivo => {
        const lat = parseFloat(dispositivo.latitude.toString());
        const lng = parseFloat(dispositivo.longitude.toString());
        return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
      });

      setDispositivosSsem(validDispositivos);
      setLastUpdate(new Date());
      
      if (validDispositivos.length === 0) {
        setError('No hay dispositivos Skyangel con coordenadas válidas');
      }
      
    } catch (error: any) {
      console.error('❌ Error cargando dispositivos Skyangel:', error);
      setError(error.message || 'Error al cargar dispositivos Skyangel');
      setDispositivosSsem([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Función para cargar productos con ubicación
  const cargarProductos = useCallback(async () => {
    console.log('🔄 INICIANDO CARGA DE PRODUCTOS...');
    console.log('🔑 Token en localStorage:', localStorage.getItem('token')?.substring(0, 20) + '...');
    setLoading(true);
    setError(null);

    try {
      const productosData = await productosApi.getProductosConUbicacion();
      console.log('📦 Productos obtenidos del API:', productosData);
      console.log('📦 Tipo de datos:', typeof productosData, Array.isArray(productosData));
      console.log('📦 Cantidad de productos:', productosData?.length || 0);
      
      if (!productosData || productosData.length === 0) {
        console.warn('⚠️ No hay productos disponibles');
        setProductos([]);
        setError('No hay productos con ubicación disponibles para tu plataforma');
        setLastUpdate(new Date());
        setLoading(false);
        return;
      }
      
      // Filtrar productos con coordenadas válidas
      const validProductos = productosData.filter(producto => {
        const lat = parseFloat(producto.latitude);
        const lng = parseFloat(producto.longitude);
        const isValid = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        if (!isValid) {
          console.warn('⚠️ Producto con coordenadas inválidas:', producto);
        }
        return isValid;
      });

      setProductos(validProductos);
      setLastUpdate(new Date());
      setError(validProductos.length === 0 ? 'No hay productos con ubicación válida' : null);
      
    } catch (error: any) {
      console.error('❌ Error cargando productos:', error);
      setError(error.message || 'Error al cargar productos con ubicación');
      setProductos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Función simplificada para cargar dispositivos CON renovación de token
  const cargarDispositivos = useCallback(async () => {
    console.log('Cargando tags para el mapa...');
    setLoading(true);
    setError(null);

    // Cargar ambas fuentes en paralelo
    const [blacResult, tracksolidResult] = await Promise.allSettled([
      (async () => {
        await refreshBlacTokenIfNeeded();
        const data = await getBlacDevices();
        return (data || []).filter(device => {
          const lat = parseFloat(device.latitude);
          const lng = parseFloat(device.longitude);
          return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        }).map(d => ({ ...d, _fuente: 'blac' as const }));
      })(),
      (async () => {
        const tags = await tracksolidApi.getAllTags();
        // Convertir tags Tracksolid al formato BlacDevice para reutilizar marcadores
        return tags
          .filter(t => t.latitude && t.longitude && Number(t.latitude) !== 0 && Number(t.longitude) !== 0)
          .map(t => ({
            name_device: t.device_name || t.imei,
            plate: t.plate || t.imei,
            vin: t.imei,
            event: t.pos_type || '',
            longitude: String(t.ultima_posicion?.longitude || t.longitude || '0'),
            latitude: String(t.ultima_posicion?.latitude || t.latitude || '0'),
            speed: String(t.speed || '0'),
            odometer: '0',
            address: '',
            dateTime: t.dateTime || '',
            _fuente: 'tracksolid' as const,
            _tracksolidId: t.id,
          } as BlacDeviceConFuente));
      })(),
    ]);

    const blacDevices = blacResult.status === 'fulfilled' ? blacResult.value : [];
    const tracksolidDevices = tracksolidResult.status === 'fulfilled' ? tracksolidResult.value : [];
    const allDevices = [...blacDevices, ...tracksolidDevices];

    setDevices(allDevices);
    setLastUpdate(new Date());
    setError(allDevices.length === 0 ? 'No hay dispositivos con ubicacion valida' : null);
    setLoading(false);
  }, []);

  // ✅ RESTAURADO: Inicializar renovación automática del token
  useEffect(() => {
    console.log('🔄 Iniciando renovación automática del token de Blac (cada 9 min)');
    const interval = startTokenRefresh();
    return () => {
      console.log('🛑 Deteniendo renovación automática del token');
      if (interval) stopTokenRefresh(interval);
    };
  }, []);

  // ✅ Cargar dispositivos cuando el mapa esté listo
  useEffect(() => {
    if (isLoaded) {
      console.log('✅ Google Maps cargado, iniciando carga de datos');
      if (showDeviceType === 'dispositivos') {
        cargarDispositivosSsem();
      } else if (showDeviceType === 'productos') {
        cargarProductos();
      } else {
        cargarDispositivos();
      }
    }
  }, [isLoaded, showDeviceType, cargarDispositivos, cargarDispositivosSsem, cargarProductos]);

  // ✅ RESTAURADO: Auto-refresh cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && isLoaded) {
        console.log('🔄 Auto-refresh de datos (cada 2 min)');
        if (showDeviceType === 'dispositivos') {
          cargarDispositivosSsem();
        } else if (showDeviceType === 'productos') {
          cargarProductos();
        } else {
          cargarDispositivos();
        }
      }
    }, 2 * 60 * 1000); // 2 minutos

    return () => clearInterval(interval);
  }, [loading, isLoaded, showDeviceType, cargarDispositivos, cargarDispositivosSsem, cargarProductos]);

  // ✅ Limpiar selecciones al cambiar tipo de vista
  useEffect(() => {
    setSelectedDevice(null);
    setSelectedDispositivo(null);
    setSelectedProducto(null);
    setShowTrazabilidad(false);
    setTrazabilidadData([]);
    setSelectedTagForTrazabilidad(null);
  }, [showDeviceType]);

  // Funcion para cargar trazabilidad de una tag (Blac o Tracksolid)
  const cargarTrazabilidad = useCallback(async (tagId: number, fuente: 'blac' | 'tracksolid' = 'blac', fechaInicio?: string, fechaFin?: string) => {
    console.log(`Cargando trazabilidad para tag ${tagId} (fuente: ${fuente})`);
    
    clearNativeElements();
    setShowTrazabilidad(false);
    setTrazabilidadData([]);
    setSelectedTagForTrazabilidad(null);
    setLoadingTrazabilidad(true);
    
    try {
      let puntosValidos: TrazabilidadPunto[] = [];

      if (fuente === 'tracksolid') {
        const response = await tracksolidApi.getTrazabilidad(tagId, fechaInicio, fechaFin);
        puntosValidos = (response.trazabilidad || [])
          .filter(p => {
            const lat = parseFloat(String(p.latitude));
            const lng = parseFloat(String(p.longitude));
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
          })
          .map(p => ({
            id: p.id,
            longitude: p.longitude,
            latitude: p.latitude,
            speed: p.speed || 0,
            odometer: 0,
            event: p.event,
            dateTime_blac: p.dateTime_tracksolid || '',
            fecha_registro: p.fecha_registro,
            distancia_recorrida: p.distancia_recorrida,
            tiempo_transcurrido: p.tiempo_transcurrido,
            velocidad_promedio: p.velocidad_promedio,
            tipo_evento: p.tipo_evento as TrazabilidadPunto['tipo_evento'],
          }))
          .sort((a, b) => new Date(a.fecha_registro).getTime() - new Date(b.fecha_registro).getTime());
      } else {
        const response = await getTagTrazabilidad(tagId);
        if (response.success && response.trazabilidad) {
          puntosValidos = response.trazabilidad
            .filter(p => {
              const lat = parseFloat(String(p.latitude));
              const lng = parseFloat(String(p.longitude));
              return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
            })
            .sort((a, b) => new Date(a.fecha_registro).getTime() - new Date(b.fecha_registro).getTime());
        }
      }
      
      console.log(`${puntosValidos.length} puntos validos (${fuente})`);
      
      // Filtrar por fechas si se proporcionan
      if (fechaInicio || fechaFin) {
        const inicio = fechaInicio ? new Date(fechaInicio + 'T00:00:00').getTime() : 0;
        const fin = fechaFin ? new Date(fechaFin + 'T23:59:59').getTime() : Infinity;
        puntosValidos = puntosValidos.filter(p => {
          const t = new Date(p.fecha_registro).getTime();
          return t >= inicio && t <= fin;
        });
        console.log(`${puntosValidos.length} puntos después de filtro de fechas`);
      }

      if (puntosValidos.length > 0) {
        setTrazabilidadData(puntosValidos);
        setSelectedTagForTrazabilidad(tagId);
        setTrazabilidadFuente(fuente);
        setShowTrazabilidad(true);
      } else {
        setTrazabilidadData([]);
        setShowTrazabilidad(false);
      }
    } catch (error) {
      console.error('Error cargando trazabilidad:', error);
      setTrazabilidadData([]);
      setShowTrazabilidad(false);
      setSelectedTagForTrazabilidad(null);
    } finally {
      setLoadingTrazabilidad(false);
    }
  }, []);

  // Funcion para mostrar trazabilidad del dispositivo seleccionado
  const handleMostrarTrazabilidad = async () => {
    if (!selectedDispositivo) return;
    await cargarTrazabilidad(selectedDispositivo.tag_id);
  };

  // Funcion para mostrar trazabilidad de la tag seleccionada en el mapa
  const handleMostrarTrazabilidadTag = async () => {
    if (!selectedDevice) return;
    
    const device = selectedDevice as BlacDeviceConFuente;
    
    if (device._fuente === 'tracksolid' && device._tracksolidId) {
      await cargarTrazabilidad(device._tracksolidId, 'tracksolid');
      return;
    }
    
    // Para Blac: buscar el ID de la tag por plate
    try {
      const resp = await fetch(`${API}/tags/buscar-por-plate/${encodeURIComponent(device.plate)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success && data.data?.id) {
          await cargarTrazabilidad(data.data.id, 'blac');
        }
      }
    } catch (err) {
      console.error('Error buscando tag por plate:', err);
    }
  };

  // ✅ Función para mostrar trazabilidad del producto seleccionado
  const handleMostrarTrazabilidadProducto = async () => {
    if (!selectedProducto) return;
    
    if (!selectedProducto.tag_plate || selectedProducto.tag_plate.trim() === '') {
      setError('Este producto no tiene una tag asignada para mostrar trazabilidad');
      return;
    }
    
    // Si el producto tiene tag Tracksolid, usar directamente
    if (selectedProducto.tag_fuente === 'tracksolid' && selectedProducto.tag_tracksolid_id) {
      await cargarTrazabilidad(selectedProducto.tag_tracksolid_id, 'tracksolid', trazFechaInicio || undefined, trazFechaFin || undefined);
      return;
    }
    
    // Para Blac: buscar el ID de la tag por plate
    try {
      const resp = await fetch(`${API}/tags/buscar-por-plate/${encodeURIComponent(selectedProducto.tag_plate)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success && data.data?.id) {
          await cargarTrazabilidad(data.data.id, 'blac', trazFechaInicio || undefined, trazFechaFin || undefined);
          return;
        }
      }
      setError('No se pudo obtener la informacion de la tag');
    } catch (error) {
      console.error('Error en trazabilidad de producto:', error);
      setError('Error al cargar la trazabilidad del producto');
    }
  };

  // ✅ Función para ocultar trazabilidad
  const handleOcultarTrazabilidad = () => {
    console.log('👁️ OCULTANDO TRAZABILIDAD - MÉTODO NATIVO');
    console.log('Estado antes:', { 
      showTrazabilidad, 
      trazabilidadData: trazabilidadData.length,
      nativePolylineExists: !!nativePolylineRef.current,
      nativeMarkersCount: nativeMarkersRef.current.length
    });
    
    // 1. Eliminar elementos nativos INMEDIATAMENTE
    clearNativeElements();
    
    // 2. Limpiar todos los estados
    setShowTrazabilidad(false);
    setTrazabilidadData([]);
    setSelectedTagForTrazabilidad(null);
    
    console.log('👁️ TRAZABILIDAD OCULTADA - MÉTODO NATIVO COMPLETADO');
  };

  // ✅ Función para obtener el path de la trazabilidad para Polyline
  const getTrazabilidadPath = useCallback(() => {
    if (!showTrazabilidad || trazabilidadData.length === 0) {
      console.log('🛤️ getTrazabilidadPath: No hay datos o trazabilidad desactivada');
      return [];
    }
    
    const path = trazabilidadData.map(punto => ({
      lat: parseFloat(punto.latitude.toString()),
      lng: parseFloat(punto.longitude.toString())
    }));
    
    console.log(`🛤️ getTrazabilidadPath: Generando path con ${path.length} puntos`);
    return path;
  }, [trazabilidadData, showTrazabilidad]);

  // ✅ Función para crear Polyline nativa (sin React)
  const createNativePolyline = useCallback((path: { lat: number; lng: number }[]) => {
    if (!mapRef.current || !window.google?.maps || path.length < 2) {
      console.log('❌ No se puede crear polyline nativa: mapa no disponible o path insuficiente');
      return;
    }

    console.log('🔧 Creando Polyline nativa con', path.length, 'puntos');

    // Crear polyline usando API nativa de Google Maps
    const polyline = new window.google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: '#2563EB',
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: mapRef.current
    });

    nativePolylineRef.current = polyline;
    console.log('✅ Polyline nativa creada y agregada al mapa');
  }, []);

  // ✅ Función para crear marcadores nativos
  const createNativeMarkers = useCallback(() => {
    if (!mapRef.current || !window.google?.maps || trazabilidadData.length === 0) {
      return;
    }

    console.log('🔧 Creando marcadores nativos');

    trazabilidadData.forEach((punto, index) => {
      const lat = parseFloat(punto.latitude.toString());
      const lng = parseFloat(punto.longitude.toString());
      
      // Mostrar solo algunos puntos
      const shouldShowMarker = index === 0 || index === trazabilidadData.length - 1 || index % 5 === 0;
      
      if (!shouldShowMarker) return;

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: index === 0 ? 8 : index === trazabilidadData.length - 1 ? 8 : 4,
          fillColor: index === 0 ? '#10B981' : index === trazabilidadData.length - 1 ? '#EF4444' : '#2563EB',
          fillOpacity: 0.8,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        title: `${index === 0 ? 'Inicio' : index === trazabilidadData.length - 1 ? 'Fin' : 'Punto'}: ${new Date(punto.fecha_registro).toLocaleString()}`
      });

      nativeMarkersRef.current.push(marker);
    });

    console.log('✅', nativeMarkersRef.current.length, 'marcadores nativos creados');
  }, [trazabilidadData]);

  // ✅ Función para eliminar TODOS los elementos nativos
  const clearNativeElements = useCallback(() => {
    console.log('🧹 ELIMINANDO ELEMENTOS NATIVOS');

    // Eliminar polyline nativa
    if (nativePolylineRef.current) {
      console.log('🗑️ Eliminando polyline nativa');
      nativePolylineRef.current.setMap(null);
      nativePolylineRef.current = null;
    }

    // Eliminar todos los marcadores nativos
    nativeMarkersRef.current.forEach((marker, index) => {
      console.log(`🗑️ Eliminando marcador nativo ${index + 1}`);
      marker.setMap(null);
    });
    nativeMarkersRef.current = [];

    console.log('✅ Todos los elementos nativos eliminados');
  }, []);

  // ✅ Función para mostrar trazabilidad nativa
  const showNativeTrazabilidad = useCallback(() => {
    if (!showTrazabilidad || trazabilidadData.length <= 1 || !selectedTagForTrazabilidad) {
      return;
    }

    console.log('🛤️ Mostrando trazabilidad nativa');

    // Limpiar elementos existentes primero
    clearNativeElements();

    // Crear path para la polyline
    const path = trazabilidadData.map(punto => ({
      lat: parseFloat(punto.latitude.toString()),
      lng: parseFloat(punto.longitude.toString())
    }));

    // Crear polyline nativa
    createNativePolyline(path);

    // Crear marcadores nativos
    createNativeMarkers();

    console.log('✅ Trazabilidad nativa mostrada');
  }, [showTrazabilidad, trazabilidadData, selectedTagForTrazabilidad, clearNativeElements, createNativePolyline, createNativeMarkers]);

  // ✅ useEffect para manejar la trazabilidad nativa
  useEffect(() => {
    if (showTrazabilidad && trazabilidadData.length > 1 && selectedTagForTrazabilidad) {
      // Mostrar trazabilidad nativa solo si hay datos válidos
      showNativeTrazabilidad();
    } else if (!showTrazabilidad) {
      // Ocultar trazabilidad nativa solo si showTrazabilidad es false
      clearNativeElements();
    }
    // Nota: No ocultamos si selectedDispositivo cambia, solo si showTrazabilidad es false
  }, [showTrazabilidad, trazabilidadData, selectedTagForTrazabilidad, showNativeTrazabilidad, clearNativeElements]);

  // ✅ Componente TrazabilidadComponent (AHORA VACÍO - usa elementos nativos)
  const TrazabilidadComponent = useCallback(() => {
    // Ya no renderizamos nada aquí - todo se maneja con elementos nativos
    console.log('🔍 TrazabilidadComponent: Usando elementos nativos - NO RENDERIZANDO componentes React');
    return null;
  }, []);

  // ✅ Debug: Monitorear cambios en el estado de trazabilidad
  useEffect(() => {
    console.log('🔍 ESTADO DE TRAZABILIDAD CAMBIÓ:', {
      showTrazabilidad,
      trazabilidadDataLength: trazabilidadData.length,
      selectedTagForTrazabilidad,
      selectedDispositivoTagId: selectedDispositivo?.tag_id,
      selectedProductoTagPlate: selectedProducto?.tag_plate,
      selectedProductoId: selectedProducto?.id,
      loadingTrazabilidad,
      nativePolylineExists: !!nativePolylineRef.current,
      nativeMarkersCount: nativeMarkersRef.current.length
    });
    
    // Si la trazabilidad se oculta inesperadamente, mostrar stack trace
    if (!showTrazabilidad && trazabilidadData.length > 0) {
      console.warn('⚠️ TRAZABILIDAD SE OCULTÓ INESPERADAMENTE!');
      console.trace('Stack trace de cuando se ocultó la trazabilidad');
    }
  }, [showTrazabilidad, trazabilidadData.length, selectedTagForTrazabilidad, selectedDispositivo?.tag_id, selectedProducto?.tag_plate, selectedProducto?.id, loadingTrazabilidad]);

  // ✅ Limpiar trazabilidad cuando se seleccione un dispositivo DIFERENTE (no cuando se cierre InfoWindow)
  useEffect(() => {
    if (selectedDispositivo && selectedTagForTrazabilidad && 
        selectedTagForTrazabilidad !== selectedDispositivo.tag_id && 
        showTrazabilidad) {
      console.log('🔄 Dispositivo diferente seleccionado, limpiando trazabilidad anterior');
      console.log(`Trazabilidad actual: tag ${selectedTagForTrazabilidad}, Dispositivo seleccionado: tag ${selectedDispositivo.tag_id}`);
      handleOcultarTrazabilidad();
    }
  }, [selectedDispositivo?.tag_id, selectedTagForTrazabilidad, showTrazabilidad]);

  // ✅ Limpiar trazabilidad cuando se seleccione un producto DIFERENTE
  useEffect(() => {
    // Solo limpiar si hay trazabilidad activa Y se selecciona un producto diferente al que generó la trazabilidad
    if (selectedProducto && selectedTagForTrazabilidad && showTrazabilidad) {
      console.log('🔄 Verificando si limpiar trazabilidad por cambio de producto');
      console.log(`Trazabilidad actual: tag ${selectedTagForTrazabilidad}, Producto seleccionado: tag ${selectedProducto.tag_plate}`);
      
      // Necesitamos obtener el tag_id del producto para comparar correctamente
      // Por ahora, NO limpiamos automáticamente para productos
      // El usuario puede usar el botón "Ocultar Trazabilidad" si quiere cambiar
      console.log('ℹ️ Manteniendo trazabilidad activa para productos (usar botón Ocultar si se desea cambiar)');
    }
  }, [selectedProducto?.id, selectedTagForTrazabilidad, showTrazabilidad]);

  // ✅ Función para refrescar manualmente CON verificación de token
  const handleRefresh = async () => {
    console.log('🔄 Refresh manual solicitado');
    
    if (showDeviceType === 'dispositivos') {
      await cargarDispositivosSsem();
    } else if (showDeviceType === 'productos') {
      await cargarProductos();
    } else {
      // Verificar token antes del refresh manual para tags Blac
      try {
        console.log('🔍 Verificando token antes del refresh manual');
        await refreshBlacTokenIfNeeded();
      } catch (error) {
        console.error('⚠️ Error verificando token en refresh manual:', error);
      }
      
      cargarDispositivos();
    }
  };

  // ✅ Funciones auxiliares simplificadas
  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return 'N/A';
    const year = dateTimeString.substring(0, 4);
    const month = dateTimeString.substring(4, 6);
    const day = dateTimeString.substring(6, 8);
    const hour = dateTimeString.substring(8, 10);
    const minute = dateTimeString.substring(10, 12);
    const second = dateTimeString.substring(12, 14);
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  };

  const getDeviceIcon = () => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="14" fill="#10B981" stroke="#ffffff" stroke-width="2"/>
        <text x="16" y="20" text-anchor="middle" font-size="16" fill="white">🏷️</text>
      </svg>
    `)}`,
    scaledSize: new window.google.maps.Size(32, 32),
    anchor: new window.google.maps.Point(16, 16)
  });

  const getDispositivoIcon = () => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <circle cx="18" cy="18" r="16" fill="#ffffff" stroke="#10B981" stroke-width="3"/>
        <rect x="12" y="8" width="12" height="20" rx="2" fill="#10B981"/>
        <rect x="13" y="10" width="10" height="14" fill="#ffffff"/>
        <circle cx="18" cy="26" r="1" fill="#ffffff"/>
        <rect x="15" y="9" width="6" height="1" rx="0.5" fill="#10B981"/>
      </svg>
    `)}`,
    scaledSize: new window.google.maps.Size(36, 36),
    anchor: new window.google.maps.Point(18, 18)
  });

  const getProductoIcon = () => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <circle cx="18" cy="18" r="16" fill="#10B981" stroke="#065F46" stroke-width="2"/>
        <rect x="10" y="12" width="16" height="12" rx="2" fill="white"/>
        <rect x="12" y="14" width="12" height="2" fill="#10B981"/>
        <rect x="12" y="17" width="8" height="1" fill="#10B981"/>
        <rect x="12" y="19" width="6" height="1" fill="#10B981"/>
        <circle cx="28" cy="8" r="6" fill="#F59E0B" stroke="#92400E" stroke-width="1"/>
        <text x="28" y="12" text-anchor="middle" fill="white" font-size="8" font-weight="bold">P</text>
      </svg>
    `)}`,
    scaledSize: new window.google.maps.Size(36, 36),
    anchor: new window.google.maps.Point(18, 18)
  });

  // ✅ Manejo de errores de Google Maps
  if (loadError) {
    return (
      <div className="h-screen flex items-center justify-center bg-red-100">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-red-800 mb-2">Error cargando Google Maps</h2>
          <p className="text-red-600">{loadError.message}</p>
        </div>
      </div>
    );
  }

  // ✅ Loading de Google Maps
  if (!isLoaded) {
    return (
      <div className="h-screen flex items-center justify-center bg-blue-100">
        <div className="text-center p-8">
          <div className="animate-spin text-4xl mb-4">🗺️</div>
          <h2 className="text-xl font-semibold text-blue-700">Cargando Google Maps...</h2>
        </div>
      </div>
    );
  }

  // ✅ Renderizado principal - SIEMPRE se muestra el mapa
  console.log('🗺️ Renderizando mapa con', devices.length, 'dispositivos');

  return (
    <div className="h-screen flex flex-col">
      {/* Header simplificado */}
      <div className="bg-black text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">
              Mapa de Productos
            </h1>
            <p className="text-sm text-gray-300">
              {productos.length} productos con ubicación
              {lastUpdate && ` • ${lastUpdate.toLocaleTimeString()}`}
              {showTrazabilidad && (
                <span className="ml-2 px-2 py-1 bg-blue-600 rounded text-xs">
                  Trazabilidad: {trazabilidadData.length} puntos
                </span>
              )}
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Botones de trazabilidad para dispositivos */}
            {selectedDispositivo && !showTrazabilidad && (
              <button
                onClick={handleMostrarTrazabilidad}
                disabled={loadingTrazabilidad}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded-lg"
              >
                <Route className={`w-4 h-4 ${loadingTrazabilidad ? 'animate-pulse' : ''}`} />
                <span>{loadingTrazabilidad ? 'Cargando...' : 'Ver Trazabilidad'}</span>
              </button>
            )}

            {/* Botones de trazabilidad para productos */}
            {selectedProducto && !showTrazabilidad && (
              <div className="flex items-center space-x-2 flex-wrap gap-y-2">
                <input
                  type="date"
                  value={trazFechaInicio}
                  onChange={e => setTrazFechaInicio(e.target.value)}
                  className="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 focus:border-blue-500 focus:outline-none"
                  placeholder="Desde"
                />
                <input
                  type="date"
                  value={trazFechaFin}
                  onChange={e => setTrazFechaFin(e.target.value)}
                  className="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 focus:border-blue-500 focus:outline-none"
                  placeholder="Hasta"
                />
                <button
                  onClick={handleMostrarTrazabilidadProducto}
                  disabled={loadingTrazabilidad}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-4 py-2 rounded-lg"
                >
                  <Route className={`w-4 h-4 ${loadingTrazabilidad ? 'animate-pulse' : ''}`} />
                  <span>{loadingTrazabilidad ? 'Cargando...' : 'Ver Trazabilidad del Producto'}</span>
                </button>
              </div>
            )}
            
            {/* Mostrar botón "Ocultar Trazabilidad" siempre que haya trazabilidad activa */}
            {showTrazabilidad && (
              <button
                onClick={handleOcultarTrazabilidad}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg"
              >
                <EyeOff className="w-4 h-4" />
                <span>Ocultar Trazabilidad</span>
              </button>
            )}
            
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-4 py-2 rounded-lg"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Actualizar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mensajes de estado */}
      {error && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 mx-4 mt-2">
          <span>📭 {error}</span>
        </div>
      )}

      {loading && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 mx-4 mt-2">
          <span>🔄 Cargando dispositivos...</span>
        </div>
      )}

      {/* Mapa - SIEMPRE VISIBLE */}
      <div className="flex-1">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={MAP_CONFIG.center}
          zoom={MAP_CONFIG.zoom}
          onLoad={(map) => {
            console.log('✅ Mapa cargado exitosamente');
            mapRef.current = map;
          }}
        >
          {/* Marcadores de Tags Blac */}
          {showDeviceType === 'tags' && devices.map((device, index) => {
            const lat = parseFloat(device.latitude);
            const lng = parseFloat(device.longitude);
            
            return (
              <Marker
                key={`tag-${device.plate}-${index}`}
                position={{ lat, lng }}
                icon={getDeviceIcon()}
                onClick={() => {
                  setSelectedDevice(device);
                  setSelectedDispositivo(null);
                  if (mapRef.current) {
                    mapRef.current.panTo({ lat, lng });
                  }
                }}
                title={device.name_device}
              />
            );
          })}

          {/* Marcadores de Dispositivos Skyangel */}
          {showDeviceType === 'dispositivos' && dispositivosSsem.map((dispositivo, index) => {
            const lat = parseFloat(dispositivo.latitude.toString());
            const lng = parseFloat(dispositivo.longitude.toString());
            
            return (
              <Marker
                key={`dispositivo-${dispositivo.dispositivo_id}-${index}`}
                position={{ lat, lng }}
                icon={getDispositivoIcon()}
                onClick={() => {
                  setSelectedDispositivo(dispositivo);
                  setSelectedDevice(null);
                  setSelectedProducto(null);
                  if (mapRef.current) {
                    mapRef.current.panTo({ lat, lng });
                  }
                }}
                title={`${dispositivo.dispositivo_marca} ${dispositivo.dispositivo_modelo}`}
              />
            );
          })}

          {/* Marcadores de Productos */}
          {showDeviceType === 'productos' && productos.map((producto, index) => {
            const lat = parseFloat(producto.latitude);
            const lng = parseFloat(producto.longitude);
            
            return (
              <Marker
                key={`producto-${producto.id}-${index}`}
                position={{ lat, lng }}
                icon={getProductoIcon()}
                onClick={() => {
                  setSelectedProducto(producto);
                  setSelectedDevice(null);
                  setSelectedDispositivo(null);
                  if (mapRef.current) {
                    mapRef.current.panTo({ lat, lng });
                  }
                }}
                title={`${producto.tipo_producto} - Tag: ${producto.tag_plate}`}
              />
            );
          })}

          {/* Línea de Trazabilidad */}
          <TrazabilidadComponent />

          {/* InfoWindow para Tags */}
          {selectedDevice && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedDevice.latitude),
                lng: parseFloat(selectedDevice.longitude)
              }}
              onCloseClick={() => setSelectedDevice(null)}
            >
              <div className="p-3">
                <h3 className="font-bold text-lg">{selectedDevice.name_device}</h3>
                <p className="text-sm text-gray-600">Nombre: {selectedDevice.plate}</p>
                <p className="text-sm text-gray-600">Estado: {selectedDevice.event}</p>
                <p className="text-sm text-gray-600">Velocidad: {selectedDevice.speed} km/h</p>
                <p className="text-xs text-gray-500 mt-2">
                  {formatDateTime(selectedDevice.dateTime)}
                </p>
                {!showTrazabilidad && (
                  <button
                    onClick={handleMostrarTrazabilidadTag}
                    disabled={loadingTrazabilidad}
                    className="mt-2 w-full flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-xs px-3 py-1.5 rounded"
                  >
                    <Route className="w-3 h-3" />
                    {loadingTrazabilidad ? 'Cargando...' : 'Ver Trazabilidad'}
                  </button>
                )}
                {showTrazabilidad && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-sm font-medium text-blue-600 flex items-center gap-1">
                      <Route className="w-4 h-4" />
                      Trazabilidad Activa
                    </p>
                    <p className="text-xs text-gray-500">
                      {trazabilidadData.length} puntos
                    </p>
                  </div>
                )}
              </div>
            </InfoWindow>
          )}

          {/* InfoWindow para Dispositivos Skyangel */}
          {selectedDispositivo && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedDispositivo.latitude.toString()),
                lng: parseFloat(selectedDispositivo.longitude.toString())
              }}
              onCloseClick={() => setSelectedDispositivo(null)}
            >
              <div className="p-3">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-blue-600" />
                  {selectedDispositivo.dispositivo_marca} {selectedDispositivo.dispositivo_modelo}
                </h3>
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-gray-600">
                    <strong>IMEI:</strong> {selectedDispositivo.dispositivo_imei}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Color:</strong> {selectedDispositivo.dispositivo_color}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Estado:</strong> 
                    <span className={`ml-1 px-2 py-1 rounded text-xs ${
                      selectedDispositivo.dispositivo_estatus === 'activo' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedDispositivo.dispositivo_estatus}
                    </span>
                  </p>
                  <hr className="my-2" />
                  <p className="text-sm text-gray-600">
                    <strong>Tag:</strong> {selectedDispositivo.tag_name_device}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Nombre:</strong> {selectedDispositivo.tag_plate}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Velocidad:</strong> {selectedDispositivo.speed} km/h
                  </p>
                  {selectedDispositivo.address && (
                    <p className="text-sm text-gray-600">
                      <strong>Dirección:</strong> {selectedDispositivo.address}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Actualizado: {formatDateTime(selectedDispositivo.dateTime || '')}
                  </p>
                  
                  {/* Información de trazabilidad */}
                  {showTrazabilidad && selectedTagForTrazabilidad === selectedDispositivo.tag_id && (
                    <div className="mt-3 pt-2 border-t border-gray-200">
                      <p className="text-sm font-medium text-blue-600 flex items-center gap-1">
                        <Route className="w-4 h-4" />
                        Trazabilidad Activa
                      </p>
                      <p className="text-xs text-gray-500">
                        {trazabilidadData.length} puntos • Línea azul en el mapa
                      </p>
                      {trazabilidadData.length > 0 && (
                        <p className="text-xs text-gray-500">
                          Desde: {new Date(trazabilidadData[0].fecha_registro).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </InfoWindow>
          )}

          {/* InfoWindow para Productos */}
          {selectedProducto && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedProducto.latitude),
                lng: parseFloat(selectedProducto.longitude)
              }}
              onCloseClick={() => setSelectedProducto(null)}
            >
              <div className="p-2 max-w-xs">
                <div className="flex items-start gap-2">
                  <Package className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-1">
                      {selectedProducto.tipo_producto}
                    </h3>
                    <p className="text-sm text-gray-600">
                      <strong>ID:</strong> {selectedProducto.id}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Tag:</strong> {selectedProducto.tag_plate}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Dispositivo:</strong> {selectedProducto.tag_name_device}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Plataforma:</strong> {selectedProducto.plataforma.nombre}
                    </p>
                    
                    {/* Mostrar algunos datos del producto */}
                    {selectedProducto.datos && Array.isArray(selectedProducto.datos) && selectedProducto.datos.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-1">Datos del producto:</p>
                        {selectedProducto.datos.slice(0, 3).map((dato, index) => (
                          <p key={index} className="text-xs text-gray-600">
                            {dato}
                          </p>
                        ))}
                        {selectedProducto.datos.length > 3 && (
                          <p className="text-xs text-gray-500 italic">
                            ... y {selectedProducto.datos.length - 3} más
                          </p>
                        )}
                      </div>
                    )}

                    {/* Información de trazabilidad para productos */}
                    {showTrazabilidad && selectedProducto && (
                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <p className="text-sm font-medium text-green-600 flex items-center gap-1">
                          <Route className="w-4 h-4" />
                          Trazabilidad del Producto
                        </p>
                        <p className="text-xs text-gray-500">
                          {trazabilidadData.length} puntos • Línea azul en el mapa
                        </p>
                        {trazabilidadData.length > 0 && (
                          <p className="text-xs text-gray-500">
                            Desde: {new Date(trazabilidadData[0].fecha_registro).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Creado: {new Date(selectedProducto.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

// Componente principal que envuelve con GoogleMapsProvider
const MapaBlack: React.FC = () => {
  console.log('🚀 Inicializando MapaBlack con GoogleMapsProvider');
  
  return (
    <GoogleMapsProvider>
      <MapaBlackContent />
    </GoogleMapsProvider>
  );
};

export default MapaBlack;
